import { useState } from 'react';
import { formatCompact } from './TrendChart';
import { SEQ_RAMP, arcPath, polar } from './viz';

export interface HourValue {
  /** 0–23, in the reporting timezone. */
  hour: number;
  value: number;
}

interface RadialClockProps {
  hours: HourValue[];
  label: string;
  size?: number;
  /** Column header for the value in the table twin. */
  valueLabel?: string;
}

const SPOKE_GAP = 2.5;

/**
 * Activity by hour of day, on a 24-hour dial.
 *
 * <b>A circle because the data is cyclical.</b> 23:00 and 00:00 are adjacent hours; a bar chart's
 * axis cuts between them and hides every pattern that crosses midnight — which, on an Egypt-facing
 * site, is where the evening peak actually lives. The dial has no seam.
 *
 * Length carries the magnitude and the ramp reinforces it: double-encoding is normally waste, but
 * on a polar axis the eye compares radii badly, so the redundant channel is doing real work.
 *
 * Hour 0 sits at 12 o'clock and the day runs clockwise, matching every clock face a reader has
 * ever used. Only 0 / 6 / 12 / 18 are labelled — 24 labels around a 200px dial is noise.
 */
export function RadialClock({ hours, label, size = 240, valueLabel = 'Events' }: RadialClockProps) {
  const [hover, setHover] = useState<number | null>(null);

  const byHour = new Array<number>(24).fill(0);
  for (const h of hours) {
    if (Number.isInteger(h.hour) && h.hour >= 0 && h.hour < 24 && Number.isFinite(h.value)) {
      byHour[h.hour] = h.value;
    }
  }

  const max = Math.max(...byHour, 0);
  if (max <= 0) {
    return <div className="empty-state t-body">No hourly activity recorded yet.</div>;
  }

  const total = byHour.reduce((sum, v) => sum + v, 0);
  const peak = byHour.indexOf(max);

  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 20;
  // The hole has to hold "peak 18:00" without the text crossing a spoke. At 0.34 it did, and a
  // readout sitting on top of the data is worse than a shorter spoke range.
  const rInner = rOuter * 0.46;

  const spokes = byHour.map((value, hour) => {
    const from = hour * 15;
    const to = from + 15 - SPOKE_GAP;
    // Radius, never area — a spoke is read as a length here, and area-scaling it would make the
    // quiet hours look busier than they are.
    const reach = rInner + (rOuter - rInner) * (value / max);
    const step = value > 0
      ? SEQ_RAMP[Math.min(SEQ_RAMP.length - 1, Math.floor((value / max) * SEQ_RAMP.length))]!
      : 'var(--viz-deemphasis)';
    return { hour, value, path: arcPath(cx, cy, rInner, Math.max(reach, rInner + 1), from, to), fill: step };
  });

  const active = hover !== null ? spokes[hover] : null;

  return (
    <div className="clock">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={`${label} by hour. Peak at ${formatHour(peak)} with ${formatCompact(max)}.`}>
        <circle cx={cx} cy={cy} r={rOuter} className="clock-ring" />

        {spokes.map((s) => (
          <path
            key={s.hour}
            d={s.path}
            fill={s.fill}
            opacity={hover === null || hover === s.hour ? 1 : 0.3}
            onMouseEnter={() => setHover(s.hour)}
            onMouseLeave={() => setHover(null)}
          >
            <title>{`${formatHour(s.hour)} — ${formatCompact(s.value)}`}</title>
          </path>
        ))}

        {[0, 6, 12, 18].map((h) => {
          const [x, y] = polar(cx, cy, rOuter + 11, h * 15 + 7.5);
          return (
            <text key={h} x={x} y={y} textAnchor="middle" dominantBaseline="middle" className="clock-tick">
              {String(h).padStart(2, '0')}
            </text>
          );
        })}

        <text x={cx} y={cy - 3} textAnchor="middle" className="clock-hero tabular">
          {active ? formatCompact(active.value) : formatCompact(max)}
        </text>
        <text x={cx} y={cy + 13} textAnchor="middle" className="clock-sub">
          {active ? formatHour(active.hour) : `peak ${formatHour(peak)}`}
        </text>
      </svg>

      <details className="viz-table">
        <summary className="t-label">Table view</summary>
        <table className="table">
          <thead>
            <tr><th>Hour</th><th className="num">{valueLabel}</th><th className="num">Share</th></tr>
          </thead>
          <tbody>
            {spokes.map((s) => (
              <tr key={s.hour}>
                <td>{formatHour(s.hour)}</td>
                <td className="num">{s.value.toLocaleString('en-US')}</td>
                <td className="num">{total > 0 ? `${((s.value / total) * 100).toFixed(1)}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

function formatHour(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}
